// X-ray style generation (framework-agnostic, pure style-JSON transform).
//
// Ported from the 1.0 viewer's MapUtils.getXrayJson. Given a Mapbox/MapLibre
// style with an `AmznVector` vector source, produces a debug style that draws
// every source-layer's raw geometry in a distinct color (points as circles,
// lines and polygons as outlines) so you can see the underlying vector data.
// No renderer dependency — operates on the style object only.

import type { StyleJson } from "../types";

/** A small qualitative palette; falls back to a deterministic-ish color past its end. */
const PALETTE = [
    "#a6cee3", "#1f78b4", "#b2df8a", "#33a02c", "#fb9a99", "#e31a1c",
    "#fdbf6f", "#ff7f00", "#cab2d6", "#6a3d9a", "#ffff99", "#b15928",
];

type GeometryKind = "point" | "line_string" | "polygon";
const GEOMETRY_KINDS: GeometryKind[] = ["point", "line_string", "polygon"];

/** The vector source name the 1.0 stylesheets use for Amazon map data. */
const AMZN_VECTOR_SOURCE = "AmznVector";

/** Returns a palette color for an index, cycling once the palette is exhausted. */
function getColor(index: number): string {
    return PALETTE[index % PALETTE.length];
}

/** Builds a single x-ray debug layer for a source-layer + geometry kind. */
function getLayerStyle(
    color: string,
    sourceLayer: string,
    geometryKind: GeometryKind
): Record<string, any> | null {
    const base = {
        id: `${sourceLayer}_${geometryKind}`,
        source: "xray",
        "source-layer": sourceLayer,
    };

    if (geometryKind === "line_string") {
        return {
            ...base,
            type: "line",
            filter: ["==", "$type", "LineString"],
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": color, "line-width": 1, "line-opacity": 0.75 },
        };
    }
    if (geometryKind === "polygon") {
        return {
            ...base,
            type: "line",
            filter: ["==", "$type", "Polygon"],
            layout: { "line-join": "round", "line-cap": "round" },
            paint: { "line-color": color, "line-width": 1, "line-opacity": 0.75 },
        };
    }
    // point
    return {
        ...base,
        type: "circle",
        filter: ["==", "$type", "Point"],
        paint: { "circle-color": color, "circle-radius": 2.5, "circle-opacity": 0.75 },
    };
}

/**
 * Produces an x-ray debug style from a normal style.
 *
 * If the style has no `AmznVector` source, the original style is returned
 * unchanged (x-ray is a no-op). System/internal source-layers (gl-draw,
 * tilegentools) are skipped.
 */
export function getXrayJson(styleJson: StyleJson): StyleJson {
    const sources = styleJson?.sources;
    if (!sources || !sources[AMZN_VECTOR_SOURCE]) {
        return styleJson;
    }

    // Only consider layers backed by the AmznVector source. The x-ray layers
    // are rendered against the cloned AmznVector data, so pulling source-layers
    // from other sources would produce empty/incorrect x-ray layers.
    const sourceLayerIds = Array.from(
        new Set<string>(
            (styleJson.layers ?? [])
                .filter(
                    (layer: any) =>
                        layer &&
                        layer["source-layer"] &&
                        layer.source === AMZN_VECTOR_SOURCE
                )
                .map((layer: any) => layer["source-layer"] as string)
        )
    );

    const xrayLayers: Record<string, any>[] = [];
    for (const geometryKind of GEOMETRY_KINDS) {
        let colorIndex = 0;
        for (const sourceLayerId of sourceLayerIds) {
            if (
                sourceLayerId.startsWith("gl-draw-") ||
                sourceLayerId.startsWith("tilegentools_")
            ) {
                continue;
            }
            const layer = getLayerStyle(getColor(colorIndex), sourceLayerId, geometryKind);
            if (layer) {
                xrayLayers.push(layer);
            }
            colorIndex += 1;
        }
    }

    // Clone the AmznVector source under the `xray` source id.
    const sourceClone = JSON.parse(JSON.stringify(sources[AMZN_VECTOR_SOURCE]));

    return {
        version: 8,
        sources: { xray: sourceClone },
        layers: xrayLayers,
    };
}
